import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, Edit2, Trash2, Eye, Star, AlertTriangle, Shield, FileText, Download, RefreshCw, X, Check, ChevronLeft, ChevronRight, ChevronDown, BarChart3, Globe, Building2, Tag, Settings, Layers, Target, Monitor, Cloud, History, RotateCcw, Briefcase, CreditCard, User, Lock, LogOut, Users } from 'lucide-react';
import logoSvg from './assets/logo.svg';

const API_BASE = '/api';

// ============================================================================
// Utility Components
// ============================================================================

const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizeClass = size === 'lg' ? 'max-w-4xl' : size === 'xl' ? 'max-w-6xl' : size === 'sm' ? 'max-w-md' : 'max-w-2xl';
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-2xl w-full ${sizeClass} max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
};

const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Abbrechen</button>
          <button onClick={onConfirm} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Löschen</button>
        </div>
      </div>
    </div>
  );
};

const TagBadge = ({ name, bgColor, textColor, onRemove, onClick, pendingRemove, onPendingRemove }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium cursor-pointer hover:opacity-80 ${pendingRemove ? 'ring-2 ring-red-400 ring-offset-1' : ''}`}
    style={{ backgroundColor: pendingRemove ? '#fee2e2' : bgColor, color: pendingRemove ? '#dc2626' : textColor }}
    onClick={onClick}
  >
    {pendingRemove ? <span className="line-through">{name}</span> : name}
    {onRemove && (
      <span
        className={`cursor-pointer ${pendingRemove ? 'text-red-600 font-bold' : 'hover:opacity-70'}`}
        onClick={e => {
          e.stopPropagation();
          if (pendingRemove) {
            onRemove();
          } else {
            onPendingRemove && onPendingRemove();
          }
        }}
        title={pendingRemove ? "Klicken um zu entfernen" : "Klicken um Entfernung vorzubereiten"}
      >
        {pendingRemove ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
      </span>
    )}
  </span>
);

const StarRating = ({ rating, onChange, readonly = false, size = 'md' }) => {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} className={`${sizeClass} ${star <= (rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} ${!readonly ? 'cursor-pointer hover:text-yellow-400' : ''}`} onClick={() => !readonly && onChange && onChange(star)} />
      ))}
    </div>
  );
};

const Input = ({ label, ...props }) => (
  <div>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <input className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" {...props} />
  </div>
);

const Textarea = ({ label, ...props }) => (
  <div>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" rows={3} {...props} />
  </div>
);

const ColorPicker = ({ label, value, onChange }) => (
  <div>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <div className="flex gap-2 items-center">
      <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
      <input type="text" value={value} onChange={e => onChange(e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm" />
    </div>
  </div>
);

// ============================================================================
// Tag Selector Component (Inline Editable)
// ============================================================================

const TagSelector = ({ items, selectedIds, onChange, allItems, placeholder = "Hinzufügen..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pendingRemoveId, setPendingRemoveId] = useState(null);

  const available = allItems.filter(item => !selectedIds.includes(item.id));
  const filtered = available.filter(item => item.name.toLowerCase().includes(search.toLowerCase()));
  const selected = allItems.filter(item => selectedIds.includes(item.id));

  const addItem = (id) => {
    onChange([...selectedIds, id]);
    setSearch('');
    setPendingRemoveId(null);
  };

  const removeItem = (id) => {
    onChange(selectedIds.filter(i => i !== id));
    setPendingRemoveId(null);
  };

  // Clear pending state when clicking elsewhere
  const handleContainerClick = () => {
    if (pendingRemoveId) {
      setPendingRemoveId(null);
    }
  };

  return (
    <div className="relative" onClick={handleContainerClick}>
      <div className="flex flex-wrap gap-1 min-h-[28px] items-center">
        {selected.map(item => (
          <TagBadge
            key={item.id}
            name={item.name}
            bgColor={item.bg_color}
            textColor={item.text_color}
            pendingRemove={pendingRemoveId === item.id}
            onPendingRemove={() => setPendingRemoveId(item.id)}
            onRemove={() => removeItem(item.id)}
          />
        ))}
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
            placeholder={selected.length === 0 ? placeholder : '+'}
            className="w-16 px-1 py-0.5 text-xs border-0 focus:ring-0 focus:outline-none bg-transparent"
          />
          {isOpen && filtered.length > 0 && (
            <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-[150px] max-h-48 overflow-y-auto">
              {filtered.map(item => (
                <button
                  key={item.id}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  onMouseDown={() => addItem(item.id)}
                >
                  <span className="w-3 h-3 rounded" style={{ backgroundColor: item.bg_color }} />
                  {item.name}
                  {item.level && <span className="text-xs text-gray-400">L{item.level}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Status Selector (Dropdown)
// ============================================================================

const StatusSelector = ({ value, onChange, statuses }) => {
  const [isOpen, setIsOpen] = useState(false);
  const current = statuses.find(s => s.id === value);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-2 py-1 rounded text-xs font-medium cursor-pointer hover:opacity-80"
        style={{ backgroundColor: current?.bg_color || '#f3f4f6', color: current?.text_color || '#374151' }}
      >
        {current?.name || 'unknown'}
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-[140px]">
            {statuses.map(status => (
              <button
                key={status.id}
                onClick={() => { onChange(status.id); setIsOpen(false); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <span className="w-3 h-3 rounded" style={{ backgroundColor: status.bg_color }} />
                {status.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// Demo Data
// ============================================================================

const demoStatuses = [
  { id: 1, name: 'unknown', color: '#6b7280', bg_color: '#f3f4f6', text_color: '#374151', description: 'Noch nicht bewertet' },
  { id: 2, name: 'active', color: '#16a34a', bg_color: '#dcfce7', text_color: '#166534', description: 'Aktiv genutzt' },
  { id: 3, name: 'tested', color: '#2563eb', bg_color: '#dbeafe', text_color: '#1e40af', description: 'Getestet' },
  { id: 4, name: 'watchlist', color: '#ca8a04', bg_color: '#fef9c3', text_color: '#854d0e', description: 'Unter Beobachtung' },
  { id: 5, name: 'blacklisted', color: '#dc2626', bg_color: '#fee2e2', text_color: '#991b1b', description: 'Nicht empfohlen' },
];

const demoRegulators = [
  { id: 1, name: 'FCA', level: 1, bg_color: '#dcfce7', text_color: '#166534', country: 'UK' },
  { id: 2, name: 'BaFin', level: 1, bg_color: '#dcfce7', text_color: '#166534', country: 'Germany' },
  { id: 3, name: 'ASIC', level: 1, bg_color: '#dcfce7', text_color: '#166534', country: 'Australia' },
  { id: 4, name: 'CySEC', level: 2, bg_color: '#dbeafe', text_color: '#1e40af', country: 'Cyprus' },
  { id: 5, name: 'FSA', level: 3, bg_color: '#fef9c3', text_color: '#854d0e', country: 'Seychelles' },
  { id: 6, name: 'FSC', level: 5, bg_color: '#fee2e2', text_color: '#991b1b', country: 'Mauritius' },
];

const demoPlatforms = [
  { id: 1, name: 'MT4', bg_color: '#e0e7ff', text_color: '#4338ca' },
  { id: 2, name: 'MT5', bg_color: '#ede9fe', text_color: '#6d28d9' },
  { id: 3, name: 'cTrader', bg_color: '#cffafe', text_color: '#0891b2' },
];

const demoStrategies = [
  { id: 1, name: 'Scalping', bg_color: '#fee2e2', text_color: '#dc2626' },
  { id: 2, name: 'Day Trading', bg_color: '#ffedd5', text_color: '#ea580c' },
  { id: 3, name: 'EA/Algo', bg_color: '#e0e7ff', text_color: '#4f46e5' },
  { id: 4, name: 'Hedging', bg_color: '#cffafe', text_color: '#0891b2' },
];

const demoBrokers = [
  { id: 1, name: 'IC Markets', status_id: 2, status_name: 'active', status_bg: '#dcfce7', status_text: '#166534', website: 'https://icmarkets.com', rating: 4.5, custom_rating: 5, regulators: [demoRegulators[0], demoRegulators[2]], platforms: [demoPlatforms[0], demoPlatforms[1], demoPlatforms[2]], strategies: [demoStrategies[0], demoStrategies[2]], notes_count: 2, custom_warnings: null },
  { id: 2, name: 'Pepperstone', status_id: 3, status_name: 'tested', status_bg: '#dbeafe', status_text: '#1e40af', website: 'https://pepperstone.com', rating: 4.4, custom_rating: 4, regulators: [demoRegulators[0], demoRegulators[2], demoRegulators[3]], platforms: [demoPlatforms[0], demoPlatforms[1]], strategies: [demoStrategies[1]], notes_count: 1, custom_warnings: null },
  { id: 3, name: 'Go Markets', status_id: 4, status_name: 'watchlist', status_bg: '#fef9c3', status_text: '#854d0e', website: 'https://gomarkets.com', rating: 3.8, custom_rating: 2, regulators: [demoRegulators[2], demoRegulators[3]], platforms: [demoPlatforms[0], demoPlatforms[1]], strategies: [demoStrategies[0]], notes_count: 3, custom_warnings: 'Spread-Manipulation beobachtet' },
  { id: 4, name: 'Scam Broker', status_id: 5, status_name: 'blacklisted', status_bg: '#fee2e2', status_text: '#991b1b', website: null, rating: 1.2, custom_rating: 1, regulators: [demoRegulators[5]], platforms: [], strategies: [], notes_count: 5, custom_warnings: 'Keine Auszahlungen' },
];

// ============================================================================
// Main App
// ============================================================================

export default function BrokerDatabaseApp() {
  const [view, setView] = useState('brokers');
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Auth state
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('broker_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('broker_token'));
  const [showLoginModal, setShowLoginModal] = useState(false);

  const isAdmin = user?.role === 1;
  const isEditor = user?.role <= 2;
  const isViewer = user?.role <= 3;

  const [brokers, setBrokers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [regulators, setRegulators] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [lps, setLps] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const [regulatorFilter, setRegulatorFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [strategyFilter, setStrategyFilter] = useState('');
  const [lpFilter, setLpFilter] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');
  const [includeUnregulated, setIncludeUnregulated] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBrokers, setTotalBrokers] = useState(0);
  
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBrokerForm, setShowBrokerForm] = useState(false);
  const [editingBroker, setEditingBroker] = useState(null);
  
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [brokersomeSyncStatus, setBrokersomeSyncStatus] = useState(null);
  const [regulationSyncStatus, setRegulationSyncStatus] = useState(null);
  const [cleanupStatus, setCleanupStatus] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showSyncOptions, setShowSyncOptions] = useState(false);
  const [selectedSyncType, setSelectedSyncType] = useState('profiles');
  const [activeSyncSource, setActiveSyncSource] = useState('myfxbook'); // 'myfxbook', 'brokersome', 'regulations', 'cleanup', or 'database'

  // Database Sync State
  const [dbSyncConfig, setDbSyncConfig] = useState(() => {
    const saved = localStorage.getItem('broker_db_sync_config');
    return saved ? JSON.parse(saved) : { remoteUrl: '', username: 'admin', password: '' };
  });
  const [dbSyncStatus, setDbSyncStatus] = useState({ status: 'idle', preview: null, result: null, error: null });

  // API Helper
  const apiCall = async (endpoint, options = {}) => {
    try {
      const headers = { 'Content-Type': 'application/json', ...options.headers };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      const res = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers
      });
      if (res.status === 401) {
        // Token expired or invalid
        handleLogout();
        throw new Error('Session expired');
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      console.warn('API unavailable:', e.message);
      setIsOffline(true);
      throw e;
    }
  };

  // Auth functions
  const handleLogin = async (username, password, remember = false) => {
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, remember })
      });
      if (!res.ok) throw new Error('Login fehlgeschlagen');
      const data = await res.json();
      setAuthToken(data.token);
      setUser(data.user);
      localStorage.setItem('broker_token', data.token);
      localStorage.setItem('broker_user', JSON.stringify(data.user));
      setIsOffline(false);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleLogout = () => {
    setAuthToken(null);
    setUser(null);
    localStorage.removeItem('broker_token');
    localStorage.removeItem('broker_user');
  };

  // Load Data
  const loadStatuses = async () => {
    try {
      const data = await apiCall('/statuses');
      setStatuses(data);
      setIsOffline(false);
    } catch { setStatuses(demoStatuses); }
  };

  const loadRegulators = async () => {
    try {
      const data = await apiCall('/regulators');
      setRegulators(data);
    } catch { setRegulators(demoRegulators); }
  };

  const loadPlatforms = async () => {
    try {
      const data = await apiCall('/platforms');
      setPlatforms(data);
    } catch { setPlatforms(demoPlatforms); }
  };

  const loadStrategies = async () => {
    try {
      const data = await apiCall('/strategies');
      setStrategies(data);
    } catch { setStrategies(demoStrategies); }
  };

  const loadLps = async () => {
    try {
      const data = await apiCall('/liquidity-providers');
      setLps(data);
    } catch { setLps([]); }
  };

  const loadAccountTypes = async () => {
    try {
      const data = await apiCall('/account-types');
      setAccountTypes(data);
    } catch { setAccountTypes([]); }
  };

  const loadBrokers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 25 });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status_id', statusFilter);
      if (regulatorFilter) params.append('regulator_id', regulatorFilter);
      if (platformFilter) params.append('platform_id', platformFilter);
      if (strategyFilter) params.append('strategy_id', strategyFilter);
      if (lpFilter) params.append('lp_id', lpFilter);
      if (accountTypeFilter) params.append('account_type_id', accountTypeFilter);
      if (!includeUnregulated) params.append('only_regulated', 'true');
      const data = await apiCall(`/brokers?${params}`);
      setBrokers(data.brokers);
      setTotalPages(data.pages);
      setTotalBrokers(data.total);
      setIsOffline(false);
    } catch {
      setBrokers(demoBrokers);
      setTotalPages(1);
      setTotalBrokers(demoBrokers.length);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, regulatorFilter, platformFilter, strategyFilter, includeUnregulated, page]);

  const loadStats = async () => {
    try {
      const data = await apiCall('/stats');
      setStats(data);
    } catch {
      setStats({ total_brokers: 25, avg_rating: 4.1, notes_count: 15, by_status: demoStatuses.map(s => ({...s, count: 5})), top_regulators: demoRegulators, blacklisted: [demoBrokers[3]], watchlist: [demoBrokers[2]] });
    }
  };

  const loadBrokerDetail = async (id) => {
    try {
      const data = await apiCall(`/brokers/${id}`);
      setSelectedBroker(data);
      setShowDetailModal(true);
    } catch {
      const broker = brokers.find(b => b.id === id);
      setSelectedBroker({ ...broker, notes: [] });
      setShowDetailModal(true);
    }
  };

  // Quick Updates
  const updateBrokerStatus = async (brokerId, statusId) => {
    try {
      await apiCall(`/brokers/${brokerId}/status?status_id=${statusId}`, { method: 'PATCH' });
      loadBrokers();
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const updateBrokerRegulators = async (brokerId, regIds) => {
    try {
      await apiCall(`/brokers/${brokerId}/regulators`, { method: 'PATCH', body: JSON.stringify(regIds) });
      loadBrokers();
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const updateBrokerPlatforms = async (brokerId, platIds) => {
    try {
      await apiCall(`/brokers/${brokerId}/platforms`, { method: 'PATCH', body: JSON.stringify(platIds) });
      loadBrokers();
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const updateBrokerStrategies = async (brokerId, stratIds) => {
    try {
      await apiCall(`/brokers/${brokerId}/strategies`, { method: 'PATCH', body: JSON.stringify(stratIds) });
      loadBrokers();
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const updateBrokerLps = async (brokerId, lpIds) => {
    try {
      await apiCall(`/brokers/${brokerId}/liquidity-providers`, { method: 'PATCH', body: JSON.stringify({ lp_ids: lpIds }) });
      loadBrokers();
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const updateBrokerAccountTypes = async (brokerId, atIds) => {
    try {
      await apiCall(`/brokers/${brokerId}/account-types`, { method: 'PATCH', body: JSON.stringify({ account_type_ids: atIds }) });
      loadBrokers();
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const deleteBroker = async (id) => {
    try {
      await apiCall(`/brokers/${id}`, { method: 'DELETE' });
      loadBrokers();
      loadStats();
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const saveBroker = async (data) => {
    try {
      if (editingBroker?.id) {
        await apiCall(`/brokers/${editingBroker.id}`, { method: 'PUT', body: JSON.stringify(data) });
      } else {
        await apiCall('/brokers', { method: 'POST', body: JSON.stringify(data) });
      }
      setShowBrokerForm(false);
      setEditingBroker(null);
      loadBrokers();
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  // Sync functions
  const startSync = async (syncType = 'profiles') => {
    try {
      await apiCall(`/sync/myfxbook?sync_type=${syncType}`, { method: 'POST' });
      setActiveSyncSource('myfxbook');
      setShowSyncOptions(false);
      setShowSyncModal(true);
      pollSyncStatus('myfxbook');
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const startBrokersomeSync = async () => {
    try {
      await apiCall('/sync/brokersome', { method: 'POST' });
      setActiveSyncSource('brokersome');
      setShowSyncOptions(false);
      setShowSyncModal(true);
      pollSyncStatus('brokersome');
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const startRegulationSync = async () => {
    try {
      await apiCall('/sync/regulations', { method: 'POST' });
      setActiveSyncSource('regulations');
      setShowSyncOptions(false);
      setShowSyncModal(true);
      pollSyncStatus('regulations');
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const startCleanup = async () => {
    try {
      await apiCall('/cleanup/websites', { method: 'POST' });
      setActiveSyncSource('cleanup');
      setShowSyncOptions(false);
      setShowSyncModal(true);
      pollSyncStatus('cleanup');
    } catch (e) { alert('Fehler: ' + e.message); }
  };

  const openSyncOptions = () => {
    setShowSyncOptions(true);
  };

  const cancelSync = async () => {
    try {
      if (activeSyncSource === 'brokersome') {
        await apiCall('/sync/brokersome/cancel', { method: 'POST' });
      } else if (activeSyncSource === 'regulations') {
        await apiCall('/sync/regulations/cancel', { method: 'POST' });
      } else if (activeSyncSource === 'cleanup') {
        await apiCall('/cleanup/cancel', { method: 'POST' });
      } else {
        await apiCall('/sync/cancel', { method: 'POST' });
      }
    } catch (e) { console.error(e); }
  };

  const pollSyncStatus = async (source = 'myfxbook') => {
    const poll = async () => {
      try {
        let endpoint = '/sync/status';
        if (source === 'brokersome') endpoint = '/sync/brokersome/status';
        if (source === 'regulations') endpoint = '/sync/regulations/status';
        if (source === 'cleanup') endpoint = '/cleanup/status';

        const status = await apiCall(endpoint);

        if (source === 'brokersome') {
          setBrokersomeSyncStatus(status);
        } else if (source === 'regulations') {
          setRegulationSyncStatus(status);
        } else if (source === 'cleanup') {
          setCleanupStatus(status);
        } else {
          setSyncStatus(status);
        }

        if (status.status === 'running') {
          setTimeout(poll, 2000);
        } else if (status.status === 'completed') {
          loadBrokers();
          loadStats();
          loadRegulators();
          loadPlatforms();
        }
      } catch (e) { console.error(e); }
    };
    poll();
  };

  // Database Sync Functions
  const saveDbSyncConfig = (config) => {
    setDbSyncConfig(config);
    localStorage.setItem('broker_db_sync_config', JSON.stringify({ ...config, password: '' })); // Don't save password
  };

  const loginToRemote = async (url, username, password) => {
    try {
      const res = await fetch(`${url}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      if (!res.ok) throw new Error('Login fehlgeschlagen');
      const data = await res.json();
      return data.token;
    } catch (e) {
      throw new Error(`Verbindung zu ${url} fehlgeschlagen: ${e.message}`);
    }
  };

  const runDbSyncPreview = async () => {
    const { remoteUrl, username, password } = dbSyncConfig;
    if (!remoteUrl || !password) {
      setDbSyncStatus({ status: 'error', error: 'Server-URL und Passwort erforderlich' });
      return;
    }

    setDbSyncStatus({ status: 'loading', preview: null, result: null, error: null });

    try {
      // Login to remote
      const remoteToken = await loginToRemote(remoteUrl, username, password);

      // Export local data
      const localData = await apiCall('/sync/export');

      // Get preview from remote
      const previewRes = await fetch(`${remoteUrl}/api/sync/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${remoteToken}` },
        body: JSON.stringify(localData)
      });
      const remotePreview = await previewRes.json();

      // Export remote data
      const remoteExportRes = await fetch(`${remoteUrl}/api/sync/export`, {
        headers: { 'Authorization': `Bearer ${remoteToken}` }
      });
      const remoteData = await remoteExportRes.json();

      // Get preview for local
      const localPreviewRes = await fetch(`${API_BASE}/sync/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify(remoteData)
      });
      const localPreview = await localPreviewRes.json();

      setDbSyncStatus({
        status: 'preview',
        preview: { local: localPreview, remote: remotePreview },
        result: null,
        error: null,
        remoteToken,
        localData,
        remoteData
      });
    } catch (e) {
      setDbSyncStatus({ status: 'error', error: e.message });
    }
  };

  const runDbSync = async (direction = 'both') => {
    const { remoteUrl } = dbSyncConfig;
    const { remoteToken, localData, remoteData } = dbSyncStatus;

    if (!remoteToken) {
      setDbSyncStatus({ ...dbSyncStatus, status: 'error', error: 'Bitte zuerst Vorschau ausführen' });
      return;
    }

    setDbSyncStatus(prev => ({ ...prev, status: 'syncing' }));

    try {
      const results = { local: null, remote: null };

      // Push to remote (local → remote)
      if (direction === 'both' || direction === 'push') {
        const pushRes = await fetch(`${remoteUrl}/api/sync/import?local_wins=true`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${remoteToken}` },
          body: JSON.stringify(localData)
        });
        results.remote = await pushRes.json();
      }

      // Pull to local (remote → local)
      if (direction === 'both' || direction === 'pull') {
        const pullRes = await fetch(`${API_BASE}/sync/import?local_wins=${direction === 'both'}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
          body: JSON.stringify(remoteData)
        });
        results.local = await pullRes.json();
      }

      setDbSyncStatus({
        status: 'completed',
        preview: null,
        result: results,
        error: null
      });

      // Reload data
      loadBrokers();
      loadStats();
      loadRegulators();
      loadPlatforms();
      loadStrategies();
      loadLps();
      loadAccountTypes();
    } catch (e) {
      setDbSyncStatus(prev => ({ ...prev, status: 'error', error: e.message }));
    }
  };

  useEffect(() => {
    loadStatuses();
    loadRegulators();
    loadPlatforms();
    loadStrategies();
    loadLps();
    loadAccountTypes();
    loadStats();
    loadBrokers();
  }, []);

  useEffect(() => { loadBrokers(); }, [search, statusFilter, regulatorFilter, platformFilter, strategyFilter, lpFilter, accountTypeFilter, includeUnregulated, page]);

  // ============================================================================
  // Dashboard View
  // ============================================================================
  const DashboardView = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="text-3xl font-bold text-blue-600">{stats?.total_brokers || 0}</div>
          <div className="text-gray-500 text-sm">Broker gesamt</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="text-3xl font-bold text-yellow-600">{stats?.avg_rating || 0}</div>
          <div className="text-gray-500 text-sm">Ø Rating</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="text-3xl font-bold text-purple-600">{stats?.notes_count || 0}</div>
          <div className="text-gray-500 text-sm">Notizen</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <div className="text-3xl font-bold text-green-600">{regulators.length}</div>
          <div className="text-gray-500 text-sm">Regulatoren</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Status-Übersicht</h3>
          <div className="space-y-2">
            {(stats?.by_status || []).map(s => (
              <div key={s.name} className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-full text-sm" style={{ backgroundColor: s.bg_color, color: s.text_color }}>{s.name}</span>
                <span className="font-semibold">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Shield className="w-5 h-5" /> Top Regulatoren</h3>
          <div className="space-y-2">
            {(stats?.top_regulators || []).slice(0, 6).map(r => (
              <div key={r.name} className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: r.bg_color, color: r.text_color }}>{r.name}</span>
                  <span className="text-xs text-gray-400">Level {r.level}</span>
                </span>
                <span className="text-sm text-gray-600">{r.count || 0} Broker</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {(stats?.blacklisted?.length > 0 || stats?.watchlist?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-6">
          {stats?.blacklisted?.length > 0 && (
            <div className="bg-red-50 border border-red-200 p-6 rounded-xl">
              <h3 className="font-semibold text-red-800 mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Blacklisted</h3>
              {stats.blacklisted.map(b => (
                <div key={b.id} className="bg-white p-3 rounded-lg mb-2">
                  <div className="font-medium text-red-700">{b.name}</div>
                  {b.custom_warnings && <div className="text-sm text-red-600">{b.custom_warnings}</div>}
                </div>
              ))}
            </div>
          )}
          {stats?.watchlist?.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-xl">
              <h3 className="font-semibold text-yellow-800 mb-4 flex items-center gap-2"><Eye className="w-5 h-5" /> Watchlist</h3>
              {stats.watchlist.map(b => (
                <div key={b.id} className="bg-white p-3 rounded-lg mb-2">
                  <div className="font-medium text-yellow-700">{b.name}</div>
                  {b.custom_warnings && <div className="text-sm text-yellow-600">{b.custom_warnings}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ============================================================================
  // Broker List View
  // ============================================================================
  const clearFilters = () => {
    setSearchInput('');
    setStatusFilter('');
    setRegulatorFilter('');
    setPlatformFilter('');
    setStrategyFilter('');
    setIncludeUnregulated(false);
    setPage(1);
  };

  const hasActiveFilters = searchInput || statusFilter || regulatorFilter || platformFilter || strategyFilter || includeUnregulated;

  const BrokerListView = () => (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-xl shadow-sm border space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="Suchen..." value={searchInput} onChange={e => setSearchInput(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <button onClick={() => { setEditingBroker({}); setShowBrokerForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-5 h-5" /> Neuer Broker
          </button>
        </div>

        {/* Filter Row */}
        <div className="flex flex-wrap gap-3 items-center">
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className={`px-3 py-2 border rounded-lg text-sm ${statusFilter ? 'bg-green-50 border-green-300 text-green-800' : 'border-gray-300'}`}>
            <option value="">Alle Status</option>
            {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={regulatorFilter} onChange={e => { setRegulatorFilter(e.target.value); setPage(1); }} className={`px-3 py-2 border rounded-lg text-sm ${regulatorFilter ? 'bg-green-50 border-green-300 text-green-800' : 'border-gray-300'}`}>
            <option value="">Alle Regulierungen</option>
            {regulators.map(r => <option key={r.id} value={r.id}>{r.name} (L{r.level})</option>)}
          </select>
          <select value={platformFilter} onChange={e => { setPlatformFilter(e.target.value); setPage(1); }} className={`px-3 py-2 border rounded-lg text-sm ${platformFilter ? 'bg-green-50 border-green-300 text-green-800' : 'border-gray-300'}`}>
            <option value="">Alle Plattformen</option>
            {platforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={strategyFilter} onChange={e => { setStrategyFilter(e.target.value); setPage(1); }} className={`px-3 py-2 border rounded-lg text-sm ${strategyFilter ? 'bg-green-50 border-green-300 text-green-800' : 'border-gray-300'}`}>
            <option value="">Alle Strategien</option>
            {strategies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={lpFilter} onChange={e => { setLpFilter(e.target.value); setPage(1); }} className={`px-3 py-2 border rounded-lg text-sm ${lpFilter ? 'bg-green-50 border-green-300 text-green-800' : 'border-gray-300'}`}>
            <option value="">Alle Liquidity Provider</option>
            {lps.map(lp => <option key={lp.id} value={lp.id}>{lp.name} ({lp.tier})</option>)}
          </select>
          <select value={accountTypeFilter} onChange={e => { setAccountTypeFilter(e.target.value); setPage(1); }} className={`px-3 py-2 border rounded-lg text-sm ${accountTypeFilter ? 'bg-green-50 border-green-300 text-green-800' : 'border-gray-300'}`}>
            <option value="">Alle Kontotypen</option>
            {accountTypes.map(at => <option key={at.id} value={at.id}>{at.name}</option>)}
          </select>
          <label className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm cursor-pointer ${includeUnregulated ? 'bg-green-50 border-green-300 text-green-800' : 'border-gray-300 hover:bg-gray-50'}`}>
            <input
              type="checkbox"
              checked={includeUnregulated}
              onChange={e => { setIncludeUnregulated(e.target.checked); setPage(1); }}
              className="w-4 h-4 text-green-600 rounded"
            />
            <span>Auch unregulierte</span>
          </label>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg flex items-center gap-1">
              <X className="w-4 h-4" /> Filter zurücksetzen
            </button>
          )}
          <div className="ml-auto text-sm text-gray-500">
            {totalBrokers} Broker gefunden
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Broker</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Regulierung</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Plattformen</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Strat</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Info</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Laden...</td></tr>
              ) : brokers.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Keine Broker gefunden</td></tr>
              ) : brokers.map(broker => (
                <tr key={broker.id} className="hover:bg-gray-50">
                  {/* Broker Name - klickbar */}
                  <td className="px-4 py-3">
                    <button onClick={() => loadBrokerDetail(broker.id)} className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-left">
                      {broker.name}
                    </button>
                  </td>
                  
                  {/* Status - editierbar */}
                  <td className="px-4 py-3">
                    <StatusSelector
                      value={broker.status_id}
                      onChange={(id) => updateBrokerStatus(broker.id, id)}
                      statuses={statuses}
                    />
                  </td>
                  
                  {/* Regulierung - editierbar als Tags */}
                  <td className="px-4 py-3 min-w-[180px]">
                    <TagSelector
                      selectedIds={(broker.regulators || []).map(r => r.id)}
                      onChange={(ids) => updateBrokerRegulators(broker.id, ids)}
                      allItems={regulators}
                      placeholder="+Reg"
                    />
                  </td>
                  
                  {/* Plattformen */}
                  <td className="px-4 py-3 min-w-[150px]">
                    <TagSelector
                      selectedIds={(broker.platforms || []).map(p => p.id)}
                      onChange={(ids) => updateBrokerPlatforms(broker.id, ids)}
                      allItems={platforms}
                      placeholder="+Plat"
                    />
                  </td>
                  
                  {/* Strategien */}
                  <td className="px-4 py-3 min-w-[150px]">
                    <TagSelector
                      selectedIds={(broker.strategies || []).map(s => s.id)}
                      onChange={(ids) => updateBrokerStrategies(broker.id, ids)}
                      allItems={strategies}
                      placeholder="+Strat"
                    />
                  </td>

                  {/* Info */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {broker.website && (
                        <a href={broker.website} target="_blank" rel="noopener" className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200">
                          <Globe className="w-3 h-3" /> Web
                        </a>
                      )}
                      {broker.notes_count > 0 && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">{broker.notes_count} Notizen</span>
                      )}
                      {broker.custom_warnings && (
                        <AlertTriangle className="w-4 h-4 text-orange-500" title={broker.custom_warnings} />
                      )}
                    </div>
                  </td>
                  
                  {/* Aktionen */}
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => loadBrokerDetail(broker.id)} className="p-2 hover:bg-gray-100 rounded-lg" title="Details">
                        <Eye className="w-4 h-4 text-gray-600" />
                      </button>
                      <button onClick={() => { setEditingBroker(broker); setShowBrokerForm(true); }} className="p-2 hover:bg-gray-100 rounded-lg" title="Bearbeiten">
                        <Edit2 className="w-4 h-4 text-blue-600" />
                      </button>
                      <button onClick={() => setDeleteConfirm(broker)} className="p-2 hover:bg-gray-100 rounded-lg" title="Löschen">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination - always show */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            Seite {page} von {totalPages} ({totalBrokers} Broker)
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-2 py-1 border rounded-lg disabled:opacity-50 hover:bg-white text-sm"
            >
              Erste
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded-lg disabled:opacity-50 hover:bg-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="px-3 py-1 bg-white border rounded-lg text-sm font-medium">{page}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || totalPages === 0}
              className="px-3 py-1 border rounded-lg disabled:opacity-50 hover:bg-white"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages || totalPages === 0}
              className="px-2 py-1 border rounded-lg disabled:opacity-50 hover:bg-white text-sm"
            >
              Letzte
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // Enhanced Regulators View
  // ============================================================================
  const RegulatorsView = () => {
    const [countryFilter, setCountryFilter] = useState('');
    const [levelFilter, setLevelFilter] = useState('');
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [selectedRegulator, setSelectedRegulator] = useState(null);
    const [deleteConfirmReg, setDeleteConfirmReg] = useState(null);

    // Get unique countries from regulators
    const countries = [...new Set(regulators.filter(r => r.country).map(r => r.country))].sort();
    const regions = [...new Set(regulators.filter(r => r.region).map(r => r.region))].sort();

    // Filter regulators
    const filteredRegulators = regulators.filter(r => {
      if (countryFilter && r.country !== countryFilter && r.region !== countryFilter) return false;
      if (levelFilter && r.level !== parseInt(levelFilter)) return false;
      return true;
    });

    // Group by country
    const groupedByCountry = filteredRegulators.reduce((acc, reg) => {
      const country = reg.country || 'Unknown';
      if (!acc[country]) acc[country] = [];
      acc[country].push(reg);
      return acc;
    }, {});

    const saveRegulator = async (data) => {
      try {
        if (editing?.id) {
          await apiCall(`/regulators/${editing.id}`, { method: 'PUT', body: JSON.stringify(data) });
        } else {
          await apiCall('/regulators', { method: 'POST', body: JSON.stringify(data) });
        }
        loadRegulators();
        setShowForm(false);
        setEditing(null);
      } catch (e) { alert('Fehler: ' + e.message); }
    };

    const deleteRegulator = async (id) => {
      try {
        await apiCall(`/regulators/${id}`, { method: 'DELETE' });
        loadRegulators();
        setDeleteConfirmReg(null);
      } catch (e) { alert('Fehler: ' + e.message); }
    };

    // Regulator Detail Panel
    const RegulatorDetail = ({ reg }) => (
      <div className="bg-gray-50 p-4 rounded-lg space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              <span className="px-2 py-1 rounded text-sm" style={{ backgroundColor: reg.bg_color, color: reg.text_color }}>{reg.name}</span>
              <span className="text-gray-400 text-sm">Level {reg.level}</span>
            </h4>
            {reg.full_name && <p className="text-gray-600 text-sm mt-1">{reg.full_name}</p>}
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setEditing(reg); setShowForm(true); }} className="p-2 hover:bg-white rounded-lg"><Edit2 className="w-4 h-4 text-blue-600" /></button>
            <button onClick={() => setDeleteConfirmReg(reg)} className="p-2 hover:bg-white rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          {reg.country && (
            <div><span className="text-gray-500">Land:</span> <span className="font-medium">{reg.country}</span></div>
          )}
          {reg.region && (
            <div><span className="text-gray-500">Region:</span> <span className="font-medium">{reg.region}</span></div>
          )}
          {reg.compensation_limit && (
            <div><span className="text-gray-500">Schutz:</span> <span className="font-medium text-green-600">{reg.compensation_limit}</span></div>
          )}
          {reg.compensation_fund && (
            <div><span className="text-gray-500">Fonds:</span> <span className="font-medium">{reg.compensation_fund}</span></div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {reg.regulator_website && (
            <a href={reg.regulator_website} target="_blank" rel="noopener" className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">
              <Globe className="w-3 h-3" /> Offizielle Website
            </a>
          )}
          {reg.registered_brokers_url && (
            <a href={reg.registered_brokers_url} target="_blank" rel="noopener" className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200">
              <Building2 className="w-3 h-3" /> Registrierte Broker
            </a>
          )}
          {reg.brokersome_url && (
            <a href={reg.brokersome_url} target="_blank" rel="noopener" className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200">
              <Globe className="w-3 h-3" /> Brokersome
            </a>
          )}
        </div>

        {reg.description && (
          <p className="text-sm text-gray-600 border-t pt-2">{reg.description}</p>
        )}
      </div>
    );

    // Regulator Form Modal
    const RegulatorForm = () => {
      const [form, setForm] = useState(editing || { bg_color: '#f3f4f6', text_color: '#374151', level: 3 });
      return (
        <Modal isOpen={showForm} onClose={() => { setShowForm(false); setEditing(null); }} title={editing?.id ? 'Regulator bearbeiten' : 'Neuer Regulator'} size="lg">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input label="Abkürzung *" value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} placeholder="z.B. FCA" />
              <Input label="Vollständiger Name" value={form.full_name || ''} onChange={e => setForm({...form, full_name: e.target.value})} placeholder="z.B. Financial Conduct Authority" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Level (1=beste Regulierung, 5=schwächste)</label>
              <div className="flex gap-2">
                {[1,2,3,4,5].map(l => (
                  <button key={l} onClick={() => setForm({...form, level: l})} className={`w-12 h-10 rounded-lg border-2 font-bold ${form.level === l ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input label="Land" value={form.country || ''} onChange={e => setForm({...form, country: e.target.value})} placeholder="z.B. United Kingdom" />
              <Input label="Region" value={form.region || ''} onChange={e => setForm({...form, region: e.target.value})} placeholder="z.B. Europe" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input label="Offizielle Website" value={form.regulator_website || ''} onChange={e => setForm({...form, regulator_website: e.target.value})} placeholder="https://..." />
              <Input label="Registrierte Broker URL" value={form.registered_brokers_url || ''} onChange={e => setForm({...form, registered_brokers_url: e.target.value})} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input label="Anlegerschutz (Limit)" value={form.compensation_limit || ''} onChange={e => setForm({...form, compensation_limit: e.target.value})} placeholder="z.B. €20,000" />
              <Input label="Schutzfonds Name" value={form.compensation_fund || ''} onChange={e => setForm({...form, compensation_fund: e.target.value})} placeholder="z.B. FSCS" />
            </div>

            <Textarea label="Beschreibung" value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} />

            <div className="grid grid-cols-2 gap-4">
              <ColorPicker label="Hintergrundfarbe" value={form.bg_color || '#f3f4f6'} onChange={v => setForm({...form, bg_color: v})} />
              <ColorPicker label="Textfarbe" value={form.text_color || '#374151'} onChange={v => setForm({...form, text_color: v})} />
            </div>

            <div className="pt-4">
              <div className="text-sm text-gray-500 mb-2">Vorschau:</div>
              <span className="px-3 py-1 rounded text-sm" style={{ backgroundColor: form.bg_color, color: form.text_color }}>{form.name || 'REG'}</span>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2 border rounded-lg">Abbrechen</button>
              <button onClick={() => saveRegulator(form)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Speichern</button>
            </div>
          </div>
        </Modal>
      );
    };

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-xl font-semibold flex items-center gap-2"><Shield className="w-6 h-6" /> Regulatoren-Verwaltung</h2>
          <div className="flex gap-2">
            <button onClick={() => { setEditing({}); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus className="w-5 h-5" /> Neu
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white p-4 rounded-xl shadow-sm border flex flex-wrap gap-3 items-center">
          <select
            value={countryFilter}
            onChange={e => setCountryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Alle Länder/Regionen</option>
            <optgroup label="Regionen">
              {regions.map(r => <option key={r} value={r}>{r}</option>)}
            </optgroup>
            <optgroup label="Länder">
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </optgroup>
          </select>

          <select
            value={levelFilter}
            onChange={e => setLevelFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Alle Level</option>
            <option value="1">Level 1 (Top-Tier)</option>
            <option value="2">Level 2 (Gut)</option>
            <option value="3">Level 3 (Mittel)</option>
            <option value="4">Level 4 (Schwach)</option>
            <option value="5">Level 5 (Offshore)</option>
          </select>

          <div className="ml-auto text-sm text-gray-500">
            {filteredRegulators.length} Regulatoren
          </div>
        </div>

        {/* Grouped List */}
        <div className="space-y-6">
          {Object.entries(groupedByCountry).sort(([a], [b]) => a.localeCompare(b)).map(([country, regs]) => (
            <div key={country} className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <Globe className="w-4 h-4" /> {country}
                </h3>
                <span className="text-sm text-gray-500">{regs.length} Regulator{regs.length !== 1 ? 'en' : ''}</span>
              </div>
              <div className="p-4 space-y-3">
                {regs.sort((a, b) => a.level - b.level).map(reg => (
                  <RegulatorDetail key={reg.id} reg={reg} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <RegulatorForm />

        <ConfirmDialog
          isOpen={!!deleteConfirmReg}
          onClose={() => setDeleteConfirmReg(null)}
          onConfirm={() => deleteRegulator(deleteConfirmReg?.id)}
          title="Regulator löschen"
          message={`Möchten Sie "${deleteConfirmReg?.name}" wirklich löschen? Diese Aktion kann über das Aktivitätslog rückgängig gemacht werden.`}
        />
      </div>
    );
  };

  // ============================================================================
  // LPs View
  // ============================================================================
  const LPsView = () => {
    const [tierFilter, setTierFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [deleteConfirmLP, setDeleteConfirmLP] = useState(null);

    const tiers = [...new Set(lps.filter(l => l.tier).map(l => l.tier))].sort();
    const types = [...new Set(lps.filter(l => l.lp_type).map(l => l.lp_type))].sort();

    const filteredLPs = lps.filter(l => {
      if (tierFilter && l.tier !== tierFilter) return false;
      if (typeFilter && l.lp_type !== typeFilter) return false;
      return true;
    });

    const groupedByTier = filteredLPs.reduce((acc, lp) => {
      const tier = lp.tier || 'Unknown';
      if (!acc[tier]) acc[tier] = [];
      acc[tier].push(lp);
      return acc;
    }, {});

    const saveLp = async (data) => {
      try {
        if (editing?.id) {
          await apiCall(`/liquidity-providers/${editing.id}`, { method: 'PUT', body: JSON.stringify(data) });
        } else {
          await apiCall('/liquidity-providers', { method: 'POST', body: JSON.stringify(data) });
        }
        loadLps();
        setShowForm(false);
        setEditing(null);
      } catch (e) { alert('Fehler: ' + e.message); }
    };

    const deleteLp = async (id) => {
      try {
        await apiCall(`/liquidity-providers/${id}`, { method: 'DELETE' });
        loadLps();
        setDeleteConfirmLP(null);
      } catch (e) { alert('Fehler: ' + e.message); }
    };

    const LPDetail = ({ lp }) => (
      <div className="bg-gray-50 p-4 rounded-lg space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              <span className="px-2 py-1 rounded text-sm" style={{ backgroundColor: lp.bg_color, color: lp.text_color }}>{lp.name}</span>
              <span className="text-gray-400 text-sm">{lp.tier}</span>
            </h4>
            {lp.lp_type && <p className="text-gray-500 text-sm mt-1">{lp.lp_type}</p>}
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setEditing(lp); setShowForm(true); }} className="p-2 hover:bg-white rounded-lg"><Edit2 className="w-4 h-4 text-blue-600" /></button>
            <button onClick={() => setDeleteConfirmLP(lp)} className="p-2 hover:bg-white rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
          </div>
        </div>

        {lp.website && (
          <a href={lp.website} target="_blank" rel="noopener" className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">
            <Globe className="w-3 h-3" /> Website
          </a>
        )}

        {lp.description && (
          <p className="text-sm text-gray-600 border-t pt-2">{lp.description}</p>
        )}
      </div>
    );

    const LPForm = () => {
      const [form, setForm] = useState(editing || { bg_color: '#fef3c7', text_color: '#92400e', tier: 'Tier-2' });
      return (
        <Modal isOpen={showForm} onClose={() => { setShowForm(false); setEditing(null); }} title={editing?.id ? 'LP bearbeiten' : 'Neuer LP'} size="lg">
          <div className="space-y-4">
            <Input label="Name *" value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} placeholder="z.B. JP Morgan" />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tier</label>
                <select value={form.tier || ''} onChange={e => setForm({...form, tier: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                  <option value="Tier-1">Tier-1 (Banks)</option>
                  <option value="Tier-2">Tier-2 (Prime Brokers)</option>
                  <option value="Tier-3">Tier-3 (ECN/Aggregators)</option>
                  <option value="Unknown">Unknown</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Typ</label>
                <select value={form.lp_type || ''} onChange={e => setForm({...form, lp_type: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">-- Auswählen --</option>
                  <option value="Bank">Bank</option>
                  <option value="Prime Broker">Prime Broker</option>
                  <option value="ECN">ECN</option>
                  <option value="Market Maker">Market Maker</option>
                  <option value="Aggregator">Aggregator</option>
                  <option value="Technology">Technology Provider</option>
                </select>
              </div>
            </div>

            <Input label="Website" value={form.website || ''} onChange={e => setForm({...form, website: e.target.value})} placeholder="https://..." />

            <Textarea label="Beschreibung" value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} />

            <div className="grid grid-cols-2 gap-4">
              <ColorPicker label="Hintergrundfarbe" value={form.bg_color || '#fef3c7'} onChange={v => setForm({...form, bg_color: v})} />
              <ColorPicker label="Textfarbe" value={form.text_color || '#92400e'} onChange={v => setForm({...form, text_color: v})} />
            </div>

            <div className="pt-4">
              <div className="text-sm text-gray-500 mb-2">Vorschau:</div>
              <span className="px-3 py-1 rounded text-sm" style={{ backgroundColor: form.bg_color, color: form.text_color }}>{form.name || 'Beispiel LP'}</span>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2 border rounded-lg">Abbrechen</button>
              <button onClick={() => saveLp(form)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Speichern</button>
            </div>
          </div>
        </Modal>
      );
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2"><Briefcase className="w-6 h-6" /> Liquidity Providers ({lps.length})</h2>
          <button onClick={() => { setEditing({}); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-5 h-5" /> Neu
          </button>
        </div>

        <div className="flex gap-3">
          <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} className={`px-3 py-2 border rounded-lg ${tierFilter ? 'bg-amber-50 border-amber-300' : ''}`}>
            <option value="">Alle Tiers</option>
            {tiers.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className={`px-3 py-2 border rounded-lg ${typeFilter ? 'bg-amber-50 border-amber-300' : ''}`}>
            <option value="">Alle Typen</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="space-y-6">
          {Object.entries(groupedByTier).sort(([a], [b]) => a.localeCompare(b)).map(([tier, tierLps]) => (
            <div key={tier} className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="bg-amber-50 px-4 py-3 flex justify-between items-center border-b border-amber-200">
                <h3 className="font-semibold text-amber-700 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> {tier}
                </h3>
                <span className="text-sm text-amber-600">{tierLps.length} LP{tierLps.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="p-4 space-y-3">
                {tierLps.sort((a, b) => a.name.localeCompare(b.name)).map(lp => (
                  <LPDetail key={lp.id} lp={lp} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <LPForm />
        <ConfirmDialog
          isOpen={!!deleteConfirmLP}
          onClose={() => setDeleteConfirmLP(null)}
          onConfirm={() => deleteLp(deleteConfirmLP?.id)}
          title="LP löschen"
          message={`Möchten Sie "${deleteConfirmLP?.name}" wirklich löschen?`}
        />
      </div>
    );
  };

  // ============================================================================
  // Account Types View
  // ============================================================================
  const AccountTypesView = () => {
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [deleteConfirmAT, setDeleteConfirmAT] = useState(null);

    const saveAccountType = async (data) => {
      try {
        if (editing?.id) {
          await apiCall(`/account-types/${editing.id}`, { method: 'PUT', body: JSON.stringify(data) });
        } else {
          await apiCall('/account-types', { method: 'POST', body: JSON.stringify(data) });
        }
        loadAccountTypes();
        setShowForm(false);
        setEditing(null);
      } catch (e) { alert('Fehler: ' + e.message); }
    };

    const deleteAccountType = async (id) => {
      try {
        await apiCall(`/account-types/${id}`, { method: 'DELETE' });
        loadAccountTypes();
        setDeleteConfirmAT(null);
      } catch (e) { alert('Fehler: ' + e.message); }
    };

    const ATForm = () => {
      const [form, setForm] = useState(editing || { bg_color: '#dbeafe', text_color: '#1e40af' });
      return (
        <Modal isOpen={showForm} onClose={() => { setShowForm(false); setEditing(null); }} title={editing?.id ? 'Kontotyp bearbeiten' : 'Neuer Kontotyp'} size="lg">
          <div className="space-y-4">
            <Input label="Name *" value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} placeholder="z.B. ECN" />

            <div className="grid grid-cols-2 gap-4">
              <Input label="Min. Einzahlung" value={form.min_deposit || ''} onChange={e => setForm({...form, min_deposit: e.target.value})} placeholder="z.B. $500+" />
              <Input label="Features" value={form.features || ''} onChange={e => setForm({...form, features: e.target.value})} placeholder="z.B. Raw Spreads, Low Commission" />
            </div>

            <Textarea label="Beschreibung" value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} />

            <div className="grid grid-cols-2 gap-4">
              <ColorPicker label="Hintergrundfarbe" value={form.bg_color || '#dbeafe'} onChange={v => setForm({...form, bg_color: v})} />
              <ColorPicker label="Textfarbe" value={form.text_color || '#1e40af'} onChange={v => setForm({...form, text_color: v})} />
            </div>

            <div className="pt-4">
              <div className="text-sm text-gray-500 mb-2">Vorschau:</div>
              <span className="px-3 py-1 rounded text-sm" style={{ backgroundColor: form.bg_color, color: form.text_color }}>{form.name || 'Beispiel'}</span>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2 border rounded-lg">Abbrechen</button>
              <button onClick={() => saveAccountType(form)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Speichern</button>
            </div>
          </div>
        </Modal>
      );
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2"><CreditCard className="w-6 h-6" /> Kontotypen ({accountTypes.length})</h2>
          <button onClick={() => { setEditing({}); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-5 h-5" /> Neu
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Vorschau</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Min. Einzahlung</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Features</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Beschreibung</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {accountTypes.map(at => (
                <tr key={at.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="px-3 py-1 rounded text-sm font-medium" style={{ backgroundColor: at.bg_color, color: at.text_color }}>{at.name}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{at.min_deposit || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{at.features || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{at.description || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => { setEditing(at); setShowForm(true); }} className="p-2 hover:bg-gray-100 rounded-lg"><Edit2 className="w-4 h-4 text-blue-600" /></button>
                      <button onClick={() => setDeleteConfirmAT(at)} className="p-2 hover:bg-gray-100 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ATForm />
        <ConfirmDialog
          isOpen={!!deleteConfirmAT}
          onClose={() => setDeleteConfirmAT(null)}
          onConfirm={() => deleteAccountType(deleteConfirmAT?.id)}
          title="Kontotyp löschen"
          message={`Möchten Sie "${deleteConfirmAT?.name}" wirklich löschen?`}
        />
      </div>
    );
  };

  // ============================================================================
  // Login Page (Full Screen)
  // ============================================================================
  const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');
      const success = await handleLogin(username, password, remember);
      if (!success) {
        setError('Benutzername oder Passwort falsch');
      }
      setLoading(false);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src={logoSvg} alt="Logo" className="h-16 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800">Broker Database</h1>
            <p className="text-gray-500 text-sm mt-1">Melden Sie sich an, um fortzufahren</p>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl border p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Benutzername</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="admin"
                    autoFocus
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Passwort</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remember"
                  checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="remember" className="ml-2 text-sm text-gray-600">
                  Angemeldet bleiben (90 Tage)
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Anmelden...
                  </>
                ) : (
                  <>
                    <LogOut className="w-4 h-4 rotate-180" />
                    Anmelden
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-gray-400 text-xs mt-6">
            © QUND - Broker Database. Alle Rechte vorbehalten.
          </p>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Users View (Admin only)
  // ============================================================================
  const UsersView = () => {
    const [localUsers, setLocalUsers] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ username: '', password: '', role: 3 });
    const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
    const loadedRef = useRef(false);

    const loadLocalUsers = async () => {
      if (!isAdmin) return;
      try {
        const res = await fetch(`${API_BASE}/users`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setLocalUsers(data);
        }
      } catch (e) {
        console.warn('Failed to load users:', e);
      }
    };

    useEffect(() => {
      if (!loadedRef.current) {
        loadedRef.current = true;
        loadLocalUsers();
      }
    }, []);

    const saveUser = async () => {
      try {
        const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` };
        if (editing?.id) {
          await fetch(`${API_BASE}/users/${editing.id}`, { method: 'PUT', headers, body: JSON.stringify(form) });
        } else {
          await fetch(`${API_BASE}/users`, { method: 'POST', headers, body: JSON.stringify(form) });
        }
        loadLocalUsers();
        setShowForm(false);
        setEditing(null);
        setForm({ username: '', password: '', role: 3 });
      } catch (e) { alert('Fehler: ' + e.message); }
    };

    const deleteUser = async (id) => {
      try {
        await fetch(`${API_BASE}/users/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        loadLocalUsers();
        setDeleteConfirmUser(null);
      } catch (e) { alert('Fehler: ' + e.message); }
    };

    const roleColors = {
      1: { bg: '#fee2e2', text: '#991b1b', name: 'Admin' },
      2: { bg: '#dbeafe', text: '#1e40af', name: 'Editor' },
      3: { bg: '#f3f4f6', text: '#374151', name: 'Viewer' }
    };

    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold flex items-center gap-2"><Users className="w-6 h-6" /> Benutzerverwaltung ({localUsers.length})</h2>
          <button onClick={() => { setForm({ username: '', password: '', role: 3 }); setEditing(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-4 h-4" /> Neuer Benutzer
          </button>
        </div>

        <div className="bg-white rounded-xl shadow border">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Benutzer</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Rolle</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Erstellt</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Letzter Login</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {localUsers.map(u => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{u.username}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: roleColors[u.role]?.bg, color: roleColors[u.role]?.text }}>
                      {roleColors[u.role]?.name || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{u.created_at?.split('T')[0] || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{u.last_login?.split('T')[0] || 'Nie'}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => { setEditing(u); setForm({ username: u.username, password: '', role: u.role }); setShowForm(true); }} className="p-2 hover:bg-gray-100 rounded-lg" title="Bearbeiten">
                        <Edit2 className="w-4 h-4 text-blue-600" />
                      </button>
                      <button onClick={() => setDeleteConfirmUser(u)} className="p-2 hover:bg-gray-100 rounded-lg" title="Löschen" disabled={u.id === user?.id}>
                        <Trash2 className={`w-4 h-4 ${u.id === user?.id ? 'text-gray-300' : 'text-red-600'}`} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showForm && (
          <Modal isOpen={showForm} onClose={() => { setShowForm(false); setEditing(null); }} title={editing ? 'Benutzer bearbeiten' : 'Neuer Benutzer'} size="sm">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Benutzername</label>
                <input
                  value={form.username}
                  onChange={e => setForm({ ...form, username: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Passwort {editing && <span className="text-gray-400">(leer = unverändert)</span>}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required={!editing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rolle</label>
                <select
                  value={form.role}
                  onChange={e => setForm({ ...form, role: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value={1}>Admin - Vollzugriff</option>
                  <option value={2}>Editor - Bearbeiten, keine Nutzer</option>
                  <option value={3}>Viewer - Nur Ansicht</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Abbrechen</button>
                <button onClick={saveUser} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Speichern</button>
              </div>
            </div>
          </Modal>
        )}
        <ConfirmDialog
          isOpen={!!deleteConfirmUser}
          onClose={() => setDeleteConfirmUser(null)}
          onConfirm={() => deleteUser(deleteConfirmUser?.id)}
          title="Benutzer löschen"
          message={`Möchten Sie "${deleteConfirmUser?.username}" wirklich löschen?`}
        />
      </div>
    );
  };

  // ============================================================================
  // Settings Views (Status, Strategies, Platforms)
  // ============================================================================
  const SettingsView = ({ type }) => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
    const [formData, setFormData] = useState({ bg_color: '#f3f4f6', text_color: '#374151', level: 3 });

    const config = {
      statuses: { title: 'Status-Verwaltung', icon: Layers, items: statuses, setItems: setStatuses, load: loadStatuses, endpoint: '/statuses', fields: ['name', 'description', 'sort_order'], hasLevel: false },
      regulators: { title: 'Regulatoren-Verwaltung', icon: Shield, items: regulators, setItems: setRegulators, load: loadRegulators, endpoint: '/regulators', fields: ['name', 'level', 'country', 'description', 'website'], hasLevel: true },
      strategies: { title: 'Strategie-Verwaltung', icon: Target, items: strategies, setItems: setStrategies, load: loadStrategies, endpoint: '/strategies', fields: ['name', 'description'], hasLevel: false },
      platforms: { title: 'Plattform-Verwaltung', icon: Monitor, items: platforms, setItems: setPlatforms, load: loadPlatforms, endpoint: '/platforms', fields: ['name', 'description', 'website'], hasLevel: false },
    }[type];

    const Icon = config.icon;

    const saveItem = async (data) => {
      try {
        if (editing?.id) {
          await apiCall(`${config.endpoint}/${editing.id}`, { method: 'PUT', body: JSON.stringify(data) });
        } else {
          await apiCall(config.endpoint, { method: 'POST', body: JSON.stringify(data) });
        }
        config.load();
        setShowForm(false);
        setEditing(null);
      } catch (e) { alert('Fehler: ' + e.message); }
    };

    const deleteItem = async (id) => {
      try {
        await apiCall(`${config.endpoint}/${id}`, { method: 'DELETE' });
        config.load();
        setDeleteConfirmItem(null);
      } catch (e) { alert('Fehler: ' + e.message); }
    };


    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2"><Icon className="w-6 h-6" /> {config.title}</h2>
          <button onClick={() => { setEditing({}); setFormData({ bg_color: '#f3f4f6', text_color: '#374151', level: 3 }); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-5 h-5" /> Neu
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                {config.hasLevel && <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Level</th>}
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Vorschau</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Beschreibung</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {config.items.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{item.name}</td>
                  {config.hasLevel && <td className="px-4 py-3"><span className="px-2 py-1 bg-gray-100 rounded text-sm">{item.level}</span></td>}
                  <td className="px-4 py-3">
                    <span className="px-3 py-1 rounded text-sm" style={{ backgroundColor: item.bg_color, color: item.text_color }}>{item.name}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{item.description || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => { setEditing(item); setFormData({ ...item }); setShowForm(true); }} className="p-2 hover:bg-gray-100 rounded-lg"><Edit2 className="w-4 h-4 text-blue-600" /></button>
                      <button onClick={() => setDeleteConfirmItem(item)} className="p-2 hover:bg-gray-100 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Item Form Modal - inlined to prevent focus loss */}
        {showForm && (
          <Modal isOpen={showForm} onClose={() => { setShowForm(false); setEditing(null); }} title={editing?.id ? 'Bearbeiten' : 'Neu erstellen'}>
            <div className="space-y-4">
              <Input label="Name *" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} />
              {config.hasLevel && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Level (1=beste, 5=schlechteste)</label>
                  <div className="flex gap-2">
                    {[1,2,3,4,5].map(l => (
                      <button key={l} onClick={() => setFormData({...formData, level: l})} className={`w-10 h-10 rounded-lg border-2 font-bold ${formData.level === l ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>{l}</button>
                    ))}
                  </div>
                </div>
              )}
              {config.fields.includes('country') && <Input label="Land" value={formData.country || ''} onChange={e => setFormData({...formData, country: e.target.value})} />}
              {config.fields.includes('website') && <Input label="Website" value={formData.website || ''} onChange={e => setFormData({...formData, website: e.target.value})} />}
              {config.fields.includes('sort_order') && <Input label="Sortierung" type="number" value={formData.sort_order || 0} onChange={e => setFormData({...formData, sort_order: parseInt(e.target.value)})} />}
              <Textarea label="Beschreibung" value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} />
              <div className="grid grid-cols-2 gap-4">
                <ColorPicker label="Hintergrundfarbe" value={formData.bg_color || '#f3f4f6'} onChange={v => setFormData({...formData, bg_color: v})} />
                <ColorPicker label="Textfarbe" value={formData.text_color || '#374151'} onChange={v => setFormData({...formData, text_color: v})} />
              </div>
              <div className="pt-4">
                <div className="text-sm text-gray-500 mb-2">Vorschau:</div>
                <span className="px-3 py-1 rounded text-sm" style={{ backgroundColor: formData.bg_color, color: formData.text_color }}>{formData.name || 'Beispiel'}</span>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2 border rounded-lg">Abbrechen</button>
                <button onClick={() => saveItem(formData)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Speichern</button>
              </div>
            </div>
          </Modal>
        )}

        <ConfirmDialog
          isOpen={!!deleteConfirmItem}
          onClose={() => setDeleteConfirmItem(null)}
          onConfirm={() => deleteItem(deleteConfirmItem?.id)}
          title="Löschen bestätigen"
          message={`Möchten Sie "${deleteConfirmItem?.name}" wirklich löschen? Diese Aktion kann über das Aktivitätslog rückgängig gemacht werden.`}
        />
      </div>
    );
  };

  // ============================================================================
  // Activity Log View
  // ============================================================================
  const ActivityLogView = () => {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const loadLogs = async () => {
      setLoading(true);
      try {
        const data = await apiCall('/activity-log?limit=100');
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      } catch (e) {
        console.error('Failed to load logs:', e);
      }
      setLoading(false);
    };

    useEffect(() => { loadLogs(); }, []);

    const undoAction = async (logId) => {
      try {
        await apiCall(`/activity-log/${logId}/undo`, { method: 'POST' });
        loadLogs();
        // Reload relevant data
        loadStatuses();
        loadRegulators();
        loadStrategies();
        loadPlatforms();
        loadBrokers();
        loadLps();
        loadAccountTypes();
      } catch (e) {
        alert('Fehler beim Rückgängig machen: ' + e.message);
      }
    };

    const getActionIcon = (action) => {
      switch (action) {
        case 'delete': return <Trash2 className="w-4 h-4 text-red-500" />;
        case 'create': return <Plus className="w-4 h-4 text-green-500" />;
        case 'update': return <Edit2 className="w-4 h-4 text-blue-500" />;
        default: return <FileText className="w-4 h-4 text-gray-500" />;
      }
    };

    const getEntityLabel = (type) => {
      const labels = {
        broker: 'Broker',
        broker_tags: 'Broker-Tags',
        regulator: 'Regulator',
        status: 'Status',
        strategy: 'Strategie',
        platform: 'Plattform',
        lp: 'Liquidity Provider',
        account_type: 'Kontotyp'
      };
      return labels[type] || type;
    };

    const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <History className="w-6 h-6" /> Aktivitätslog
          </h2>
          <button onClick={loadLogs} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Zeitpunkt</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Aktion</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Typ</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Details</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      Noch keine Aktivitäten protokolliert
                    </td>
                  </tr>
                ) : (
                  logs.map(log => (
                    <tr key={log.id} className={`hover:bg-gray-50 ${log.undone ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(log.created_at)}</td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-2">
                          {getActionIcon(log.action)}
                          <span className="text-sm capitalize">{log.action === 'delete' ? 'Gelöscht' : log.action === 'create' ? 'Erstellt' : 'Geändert'}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-gray-100 rounded text-sm">{getEntityLabel(log.entity_type)}</span>
                      </td>
                      <td className="px-4 py-3 font-medium">{log.entity_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{log.details || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          {(log.action === 'delete' || (log.action === 'update' && log.entity_type === 'broker_tags')) && log.previous_data && !log.undone && (
                            <button
                              onClick={() => undoAction(log.id)}
                              className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="Rückgängig machen"
                            >
                              <RotateCcw className="w-4 h-4" /> Wiederherstellen
                            </button>
                          )}
                          {log.undone && (
                            <span className="text-sm text-gray-400 italic">Wiederhergestellt</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-sm text-gray-500 text-center">
          {total} Einträge insgesamt
        </p>
      </div>
    );
  };

  // ============================================================================
  // Sync Options Modal (before sync starts)
  // ============================================================================
  const SyncOptionsModal = () => {
    // Use App-level state to prevent reset on re-render
    const selectedSource = activeSyncSource;
    const setSelectedSource = setActiveSyncSource;

    if (!showSyncOptions) return null;

    return (
      <Modal isOpen={showSyncOptions} onClose={() => setShowSyncOptions(false)} title="Broker Daten synchronisieren" size="md">
        <div className="space-y-4">
          {/* Source Selection Tabs */}
          <div className="flex border-b">
            <button
              onClick={() => setSelectedSource('myfxbook')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${selectedSource === 'myfxbook' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              MyFXBook
            </button>
            <button
              onClick={() => setSelectedSource('brokersome')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${selectedSource === 'brokersome' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Brokersome
            </button>
            <button
              onClick={() => setSelectedSource('regulations')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${selectedSource === 'regulations' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Regulatoren
            </button>
            <button
              onClick={() => setSelectedSource('cleanup')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${selectedSource === 'cleanup' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Cleanup
            </button>
            <button
              onClick={() => setSelectedSource('database')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${selectedSource === 'database' ? 'border-cyan-500 text-cyan-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Datenbank
            </button>
          </div>

          {/* MyFXBook Options */}
          {selectedSource === 'myfxbook' && (
            <div className="space-y-3">
              <p className="text-gray-600 text-sm">
                Synchronisiert Broker-Daten von myfxbook.com (Rating, Regulierungen, Plattformen, Trading-Konditionen)
              </p>

              <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${selectedSyncType === 'profiles' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name="syncType"
                  value="profiles"
                  checked={selectedSyncType === 'profiles'}
                  onChange={() => setSelectedSyncType('profiles')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-800">Profile aktualisieren</div>
                  <div className="text-sm text-gray-500">
                    Aktualisiert die {totalBrokers} bestehenden Broker
                  </div>
                  <div className="text-xs text-green-600 mt-1">Empfohlen</div>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${selectedSyncType === 'full' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name="syncType"
                  value="full"
                  checked={selectedSyncType === 'full'}
                  onChange={() => setSelectedSyncType('full')}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-800">Vollständiger Sync</div>
                  <div className="text-sm text-gray-500">
                    Scraped alle Broker neu (findet neue Broker)
                  </div>
                  <div className="text-xs text-orange-600 mt-1">Dauert 1-2 Stunden</div>
                </div>
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowSyncOptions(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  Abbrechen
                </button>
                <button
                  onClick={() => startSync(selectedSyncType)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  MyFXBook Sync
                </button>
              </div>
            </div>
          )}

          {/* Brokersome Options */}
          {selectedSource === 'brokersome' && (
            <div className="space-y-3">
              <p className="text-gray-600 text-sm">
                Synchronisiert einzigartige Daten von brokersome.com:
              </p>

              <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                <div className="font-medium text-purple-800 mb-2">Exklusive Daten:</div>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• <strong>Dealing Book</strong> - A-book, B-book, Hybrid</li>
                  <li>• <strong>Liquidity Providers</strong> - Liste der LPs</li>
                  <li>• <strong>Broker Tier</strong> - Qualitätsstufe 1-3</li>
                  <li>• <strong>Popularity</strong> - Top/High/Average/Low</li>
                  <li>• <strong>Publicly Traded</strong> - Börsennotiert</li>
                  <li>• <strong>Trust Ratings</strong> - Transparenz, Service</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
                <strong>~385 Broker</strong> werden mit bestehenden Brokern gematcht und ergänzt.
                Neue Broker werden automatisch erstellt.
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowSyncOptions(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  Abbrechen
                </button>
                <button
                  onClick={startBrokersomeSync}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Brokersome Sync
                </button>
              </div>
            </div>
          )}

          {/* Regulations Options */}
          {selectedSource === 'regulations' && (
            <div className="space-y-3">
              <p className="text-gray-600 text-sm">
                Synchronisiert Regulatoren-Daten von brokersome.com/forex-brokers/regulation:
              </p>

              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <div className="font-medium text-green-800 mb-2">Regulatoren-Daten:</div>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• <strong>Vollständige Namen</strong> - z.B. "Financial Conduct Authority"</li>
                  <li>• <strong>Offizielle Websites</strong> - Links zu Behörden</li>
                  <li>• <strong>Anlegerschutz</strong> - Compensation Limits (€20,000, £85,000)</li>
                  <li>• <strong>Länder & Regionen</strong> - Geografische Zuordnung</li>
                  <li>• <strong>Schutzfonds</strong> - FSCS, ICF, SIPC, etc.</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
                <strong>~30 Regulatoren</strong> werden mit bestehenden gematcht und ergänzt.
                Neue Regulatoren werden automatisch erstellt (Level 3).
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowSyncOptions(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  Abbrechen
                </button>
                <button
                  onClick={startRegulationSync}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Regulatoren Sync
                </button>
              </div>
            </div>
          )}

          {/* Cleanup Options */}
          {selectedSource === 'cleanup' && (
            <div className="space-y-3">
              <p className="text-gray-600 text-sm">
                Prüft alle Broker-Websites auf Erreichbarkeit und markiert inaktive Broker automatisch.
              </p>

              <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                <div className="font-medium text-orange-800 mb-2">Website-Check:</div>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• Testet alle Broker mit hinterlegter Website</li>
                  <li>• Nicht erreichbare Seiten → Status "inaktiv"</li>
                  <li>• Bereits inaktive Broker werden übersprungen</li>
                  <li>• Änderungen werden im Activity Log protokolliert</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
                <strong>{totalBrokers} Broker</strong> werden auf Website-Erreichbarkeit geprüft.
                Timeout: 10 Sekunden pro Website.
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowSyncOptions(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  Abbrechen
                </button>
                <button
                  onClick={startCleanup}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Cleanup starten
                </button>
              </div>
            </div>
          )}

          {/* Database Sync Options */}
          {selectedSource === 'database' && (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Synchronisiert die Datenbank zwischen lokaler Instanz und Remote-Server mit intelligentem Merge.
              </p>

              <div className="bg-cyan-50 border border-cyan-200 p-4 rounded-lg">
                <div className="font-medium text-cyan-800 mb-2">Smart Sync Features:</div>
                <ul className="text-sm text-cyan-700 space-y-1">
                  <li>• Vergleicht Einträge anhand von Timestamps</li>
                  <li>• Neue Einträge werden hinzugefügt</li>
                  <li>• Bei Konflikten gewinnt der neuere Eintrag</li>
                  <li>• Vorschau vor dem Sync möglich</li>
                </ul>
              </div>

              {/* Server Configuration */}
              <div className="space-y-3 border-t pt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remote Server URL</label>
                  <input
                    type="text"
                    value={dbSyncConfig.remoteUrl}
                    onChange={e => saveDbSyncConfig({ ...dbSyncConfig, remoteUrl: e.target.value })}
                    placeholder="http://server-ip oder https://domain.com"
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Benutzername</label>
                    <input
                      type="text"
                      value={dbSyncConfig.username}
                      onChange={e => saveDbSyncConfig({ ...dbSyncConfig, username: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Passwort</label>
                    <input
                      type="password"
                      value={dbSyncConfig.password}
                      onChange={e => setDbSyncConfig({ ...dbSyncConfig, password: e.target.value })}
                      placeholder="Wird nicht gespeichert"
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Status/Error Display */}
              {dbSyncStatus.error && (
                <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-red-700 text-sm">
                  <strong>Fehler:</strong> {dbSyncStatus.error}
                </div>
              )}

              {dbSyncStatus.status === 'loading' && (
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-blue-700 text-sm flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Verbinde und analysiere Daten...
                </div>
              )}

              {dbSyncStatus.status === 'syncing' && (
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-blue-700 text-sm flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Synchronisiere Daten...
                </div>
              )}

              {/* Preview Results */}
              {dbSyncStatus.status === 'preview' && dbSyncStatus.preview && (
                <div className="space-y-3 border-t pt-4">
                  <div className="font-medium text-gray-800">Vorschau der Änderungen:</div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm font-medium text-gray-700 mb-2">→ Remote Server</div>
                      <div className="text-xs space-y-1">
                        <div className="text-green-600">+ {dbSyncStatus.preview.remote?.summary?.will_add || 0} neue Einträge</div>
                        <div className="text-blue-600">↻ {dbSyncStatus.preview.remote?.summary?.will_update || 0} Updates</div>
                        <div className="text-gray-500">○ {dbSyncStatus.preview.remote?.summary?.will_skip || 0} unverändert</div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm font-medium text-gray-700 mb-2">← Lokal</div>
                      <div className="text-xs space-y-1">
                        <div className="text-green-600">+ {dbSyncStatus.preview.local?.summary?.will_add || 0} neue Einträge</div>
                        <div className="text-blue-600">↻ {dbSyncStatus.preview.local?.summary?.will_update || 0} Updates</div>
                        <div className="text-gray-500">○ {dbSyncStatus.preview.local?.summary?.will_skip || 0} unverändert</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => runDbSync('push')}
                      className="px-3 py-1.5 text-sm border border-cyan-300 text-cyan-700 rounded-lg hover:bg-cyan-50"
                    >
                      Nur Push →
                    </button>
                    <button
                      onClick={() => runDbSync('pull')}
                      className="px-3 py-1.5 text-sm border border-cyan-300 text-cyan-700 rounded-lg hover:bg-cyan-50"
                    >
                      ← Nur Pull
                    </button>
                    <button
                      onClick={() => runDbSync('both')}
                      className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                    >
                      ↔ Beides
                    </button>
                  </div>
                </div>
              )}

              {/* Completed Results */}
              {dbSyncStatus.status === 'completed' && dbSyncStatus.result && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <div className="font-medium text-green-800 mb-2">✓ Sync abgeschlossen!</div>
                  <div className="text-sm text-green-700 space-y-1">
                    {dbSyncStatus.result.remote && (
                      <div>Remote: +{dbSyncStatus.result.remote.total_added} hinzugefügt, ↻{dbSyncStatus.result.remote.total_updated} aktualisiert</div>
                    )}
                    {dbSyncStatus.result.local && (
                      <div>Lokal: +{dbSyncStatus.result.local.total_added} hinzugefügt, ↻{dbSyncStatus.result.local.total_updated} aktualisiert</div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-2 border-t">
                <button onClick={() => { setShowSyncOptions(false); setDbSyncStatus({ status: 'idle', preview: null, result: null, error: null }); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  Schließen
                </button>
                <button
                  onClick={runDbSyncPreview}
                  disabled={!dbSyncConfig.remoteUrl || !dbSyncConfig.password || dbSyncStatus.status === 'loading'}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Eye className="w-4 h-4" />
                  Vorschau
                </button>
              </div>
            </div>
          )}

          {selectedSource !== 'cleanup' && selectedSource !== 'database' && (
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-yellow-700 text-sm">
              <strong>Hinweis:</strong> Ein Firefox-Fenster wird geöffnet. Nicht schließen während des Syncs.
            </div>
          )}
        </div>
      </Modal>
    );
  };

  // ============================================================================
  // Sync Modal (progress display)
  // ============================================================================
  const SyncModal = () => {
    if (!showSyncModal) return null;

    const isBrokersome = activeSyncSource === 'brokersome';
    const isRegulations = activeSyncSource === 'regulations';
    const isCleanup = activeSyncSource === 'cleanup';

    let s = syncStatus || {};
    if (isBrokersome) s = brokersomeSyncStatus || {};
    if (isRegulations) s = regulationSyncStatus || {};
    if (isCleanup) s = cleanupStatus || {};

    const isRunning = s.status === 'running';
    const isComplete = s.status === 'completed';
    const isError = s.status === 'error';

    let title = 'MyFXBook Sync';
    let sourceLabel = 'myfxbook.com';
    let colorClass = 'blue';
    if (isBrokersome) { title = 'Brokersome Sync'; sourceLabel = 'brokersome.com'; colorClass = 'purple'; }
    if (isRegulations) { title = 'Regulatoren Sync'; sourceLabel = 'brokersome.com/regulation'; colorClass = 'green'; }
    if (isCleanup) { title = 'Website Cleanup'; sourceLabel = 'Prüfe Broker-Websites'; colorClass = 'orange'; }

    return (
      <Modal isOpen={showSyncModal} onClose={() => !isRunning && setShowSyncModal(false)} title={title} size="sm">
        <div className="space-y-4">
          {/* Source indicator */}
          <div className={`text-center text-xs font-medium px-2 py-1 rounded ${isCleanup ? 'bg-orange-100 text-orange-700' : `bg-${colorClass}-100 text-${colorClass}-700`}`}>
            {sourceLabel}
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-300 ${isError ? 'bg-red-500' : isComplete ? 'bg-green-500' : isCleanup ? 'bg-orange-500' : isRegulations ? 'bg-green-600' : isBrokersome ? 'bg-purple-600' : 'bg-blue-600'}`}
              style={{ width: `${s.progress || 0}%` }}
            />
          </div>

          {/* Status */}
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-800">{s.progress || 0}%</div>
            <div className="text-sm text-gray-600">{s.message || 'Initialisiere...'}</div>
            {(s.current_broker || s.current_regulator) && isRunning && (
              <div className="text-xs text-gray-400 mt-1">Aktuell: {s.current_broker || s.current_regulator}</div>
            )}
          </div>

          {/* Cleanup Stats */}
          {isCleanup && (s.online > 0 || s.offline > 0) && (
            <div className="flex justify-center gap-3 text-sm flex-wrap">
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">{s.online || 0} Online</span>
              <span className="px-2 py-1 bg-red-100 text-red-700 rounded">{s.offline || 0} Offline</span>
              {s.errors > 0 && <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">{s.errors} Fehler</span>}
            </div>
          )}

          {/* Regular Sync Stats */}
          {!isCleanup && s.stats && (
            <div className="flex justify-center gap-3 text-sm flex-wrap">
              {s.stats.matched > 0 && <span className="px-2 py-1 bg-green-100 text-green-700 rounded">{s.stats.matched} Gematcht</span>}
              {s.stats.new > 0 && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">{s.stats.new} Neu</span>}
              {s.stats.updated > 0 && <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">{s.stats.updated} Aktualisiert</span>}
              {s.stats.failed > 0 && <span className="px-2 py-1 bg-red-100 text-red-700 rounded">{s.stats.failed} Fehler</span>}
            </div>
          )}

          {/* Error */}
          {isError && s.error && (
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-red-700 text-sm">
              {s.error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            {isRunning ? (
              <button onClick={cancelSync} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50">
                Abbrechen
              </button>
            ) : (
              <button onClick={() => setShowSyncModal(false)} className={`px-4 py-2 text-white rounded-lg ${isCleanup ? 'bg-orange-600 hover:bg-orange-700' : isRegulations ? 'bg-green-600 hover:bg-green-700' : isBrokersome ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                Schließen
              </button>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  // ============================================================================
  // Broker Detail Modal
  // ============================================================================
  const BrokerDetailModal = () => {
    if (!selectedBroker) return null;
    const b = selectedBroker;
    
    return (
      <Modal isOpen={showDetailModal} onClose={() => { setShowDetailModal(false); setSelectedBroker(null); }} title={b.name} size="lg">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: b.status_bg, color: b.status_text }}>{b.status_name || 'unknown'}</span>
                {b.website && <a href={b.website} target="_blank" rel="noopener" className="text-blue-600 hover:underline flex items-center gap-1 text-sm"><Globe className="w-4 h-4" /> Website</a>}
              </div>
              {b.regulators?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {b.regulators.map(r => <TagBadge key={r.id} name={`${r.name} (L${r.level})`} bgColor={r.bg_color} textColor={r.text_color} />)}
                </div>
              )}
            </div>
            <button onClick={() => { setEditingBroker(b); setShowDetailModal(false); setShowBrokerForm(true); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
              <Edit2 className="w-4 h-4" /> Bearbeiten
            </button>
          </div>

          {/* Rating */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-500">MFX Rating</div>
                <div className="font-semibold">{b.rating || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Eigene Bewertung</div>
                <StarRating rating={b.custom_rating} readonly size="sm" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Hauptsitz</div>
                <div className="font-medium">{b.headquarters || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Gegründet</div>
                <div className="font-medium">{b.founded_year || '-'}</div>
              </div>
            </div>
          </div>

          {/* Trading Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {b.min_deposit && <div className="bg-gray-50 p-3 rounded-lg"><div className="text-xs text-gray-500">Min. Einzahlung</div><div className="font-medium">{b.min_deposit}</div></div>}
            {b.max_leverage && <div className="bg-gray-50 p-3 rounded-lg"><div className="text-xs text-gray-500">Max. Hebel</div><div className="font-medium">{b.max_leverage}</div></div>}
            {b.spread_type && <div className="bg-gray-50 p-3 rounded-lg"><div className="text-xs text-gray-500">Spread-Typ</div><div className="font-medium">{b.spread_type}</div></div>}
          </div>

          {/* Brokersome Data */}
          {(b.dealing_book || b.lp_count || b.execution_speed_ms || b.broker_tier) && (
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-purple-800">Brokersome Daten</div>
                {b.brokersome_url && (
                  <a href={b.brokersome_url} target="_blank" rel="noopener" className="text-purple-600 hover:underline text-xs flex items-center gap-1">
                    <Globe className="w-3 h-3" /> Quelle
                  </a>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {b.dealing_book && (
                  <div>
                    <div className="text-xs text-purple-600">Dealing Book</div>
                    <div className={`font-semibold ${b.dealing_book === 'A-book' ? 'text-green-700' : b.dealing_book === 'B-book' ? 'text-red-700' : 'text-orange-700'}`}>
                      {b.dealing_book}
                    </div>
                  </div>
                )}
                {b.lp_count && (
                  <div>
                    <div className="text-xs text-purple-600">Liquiditätsprovider</div>
                    <div className="font-semibold text-gray-800">{b.lp_count} {b.lp_quality && <span className="text-xs text-gray-500">({b.lp_quality})</span>}</div>
                  </div>
                )}
                {b.execution_speed_ms && (
                  <div>
                    <div className="text-xs text-purple-600">Ausführungszeit</div>
                    <div className="font-semibold text-gray-800">{b.execution_speed_ms} ms</div>
                  </div>
                )}
                {b.broker_tier && (
                  <div>
                    <div className="text-xs text-purple-600">Broker Tier</div>
                    <div className={`font-semibold ${b.broker_tier <= 2 ? 'text-green-700' : b.broker_tier === 3 ? 'text-yellow-700' : 'text-red-700'}`}>
                      Tier {b.broker_tier}
                    </div>
                  </div>
                )}
                {b.publicly_traded !== null && b.publicly_traded !== undefined && (
                  <div>
                    <div className="text-xs text-purple-600">Börsennotiert</div>
                    <div className="font-semibold text-gray-800">{b.publicly_traded ? 'Ja' : 'Nein'}</div>
                  </div>
                )}
                {b.popularity_rating && (
                  <div>
                    <div className="text-xs text-purple-600">Popularität</div>
                    <div className="font-semibold text-gray-800">{b.popularity_rating}</div>
                  </div>
                )}
              </div>
              {/* LP Tags */}
              {b.liquidity_providers_list?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-purple-200">
                  <div className="text-xs text-purple-600 mb-2">Liquidity Providers ({b.liquidity_providers_list.length})</div>
                  <div className="flex flex-wrap gap-1">
                    {b.liquidity_providers_list.map(lp => (
                      <span key={lp.id}
                            className="px-2 py-0.5 rounded text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity"
                            style={{ backgroundColor: lp.bg_color, color: lp.text_color }}
                            onClick={() => { setLpFilter(lp.id); setSelectedBroker(null); }}
                            title={`${lp.tier} - ${lp.lp_type || 'LP'}`}>
                        {lp.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Account Types Tags */}
              {b.account_types_list?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-purple-200">
                  <div className="text-xs text-purple-600 mb-2">Kontotypen ({b.account_types_list.length})</div>
                  <div className="flex flex-wrap gap-1">
                    {b.account_types_list.map(at => (
                      <span key={at.id}
                            className="px-2 py-0.5 rounded text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity"
                            style={{ backgroundColor: at.bg_color, color: at.text_color }}
                            onClick={() => { setAccountTypeFilter(at.id); setSelectedBroker(null); }}
                            title={at.description || at.name}>
                        {at.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Platforms & Strategies */}
          <div className="grid md:grid-cols-2 gap-4">
            {b.platforms?.length > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Plattformen</div>
                <div className="flex flex-wrap gap-1">{b.platforms.map(p => <TagBadge key={p.id} name={p.name} bgColor={p.bg_color} textColor={p.text_color} />)}</div>
              </div>
            )}
            {b.strategies?.length > 0 && (
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Strategien</div>
                <div className="flex flex-wrap gap-1">{b.strategies.map(s => <TagBadge key={s.id} name={s.name} bgColor={s.bg_color} textColor={s.text_color} />)}</div>
              </div>
            )}
          </div>

          {/* Warnings */}
          {b.custom_warnings && (
            <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-orange-700 font-medium mb-1"><AlertTriangle className="w-5 h-5" /> Warnungen</div>
              <p className="text-orange-600">{b.custom_warnings}</p>
            </div>
          )}

          {/* Notes */}
          {(b.custom_notes || b.custom_experience) && (
            <div className="grid md:grid-cols-2 gap-4">
              {b.custom_notes && <div className="bg-gray-50 p-4 rounded-lg"><div className="text-sm font-medium mb-2">Notizen</div><p className="text-gray-600 text-sm whitespace-pre-wrap">{b.custom_notes}</p></div>}
              {b.custom_experience && <div className="bg-blue-50 p-4 rounded-lg"><div className="text-sm font-medium text-blue-700 mb-2">Erfahrungen</div><p className="text-blue-600 text-sm whitespace-pre-wrap">{b.custom_experience}</p></div>}
            </div>
          )}

          {/* Detailed Notes */}
          {b.notes?.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><FileText className="w-5 h-5" /> Notizen ({b.notes.length})</h4>
              <div className="space-y-2">
                {b.notes.map(n => (
                  <div key={n.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div><span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs mr-2">{n.note_type}</span><span className="font-medium">{n.title}</span></div>
                      <span className="text-xs text-gray-400">{n.created_at?.split('T')[0]}</span>
                    </div>
                    {n.content && <p className="text-gray-600 text-sm mt-2">{n.content}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  // ============================================================================
  // Broker Form Modal
  // ============================================================================
  const BrokerFormModal = () => {
    const [form, setForm] = useState(editingBroker || {});
    
    useEffect(() => {
      if (editingBroker) {
        setForm({
          ...editingBroker,
          regulator_ids: (editingBroker.regulators || []).map(r => r.id),
          platform_ids: (editingBroker.platforms || []).map(p => p.id),
          strategy_ids: (editingBroker.strategies || []).map(s => s.id),
        });
      }
    }, [editingBroker]);

    return (
      <Modal isOpen={showBrokerForm} onClose={() => { setShowBrokerForm(false); setEditingBroker(null); }} title={editingBroker?.id ? 'Broker bearbeiten' : 'Neuer Broker'} size="lg">
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Name *" value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} />
            <Input label="Website" value={form.website || ''} onChange={e => setForm({...form, website: e.target.value})} />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Hauptsitz" value={form.headquarters || ''} onChange={e => setForm({...form, headquarters: e.target.value})} />
            <Input label="Gründungsjahr" type="number" value={form.founded_year || ''} onChange={e => setForm({...form, founded_year: parseInt(e.target.value) || null})} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status_id || ''} onChange={e => setForm({...form, status_id: parseInt(e.target.value) || null})} className="w-full px-3 py-2 border rounded-lg">
                <option value="">Auswählen...</option>
                {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Min. Einzahlung" value={form.min_deposit || ''} onChange={e => setForm({...form, min_deposit: e.target.value})} />
            <Input label="Max. Hebel" value={form.max_leverage || ''} onChange={e => setForm({...form, max_leverage: e.target.value})} />
            <Input label="MFX Rating" type="number" step="0.1" value={form.rating || ''} onChange={e => setForm({...form, rating: parseFloat(e.target.value) || null})} />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Regulierungen</label>
              <select multiple value={(form.regulator_ids || []).map(String)} onChange={e => setForm({...form, regulator_ids: [...e.target.selectedOptions].map(o => parseInt(o.value))})} className="w-full px-3 py-2 border rounded-lg h-24">
                {regulators.map(r => <option key={r.id} value={r.id}>{r.name} (L{r.level})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plattformen</label>
              <select multiple value={(form.platform_ids || []).map(String)} onChange={e => setForm({...form, platform_ids: [...e.target.selectedOptions].map(o => parseInt(o.value))})} className="w-full px-3 py-2 border rounded-lg h-24">
                {platforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Strategien</label>
              <select multiple value={(form.strategy_ids || []).map(String)} onChange={e => setForm({...form, strategy_ids: [...e.target.selectedOptions].map(o => parseInt(o.value))})} className="w-full px-3 py-2 border rounded-lg h-24">
                {strategies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Eigene Bewertung</label>
            <StarRating rating={form.custom_rating} onChange={r => setForm({...form, custom_rating: r})} />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Textarea label="Notizen" value={form.custom_notes || ''} onChange={e => setForm({...form, custom_notes: e.target.value})} />
            <Textarea label="Warnungen" value={form.custom_warnings || ''} onChange={e => setForm({...form, custom_warnings: e.target.value})} />
          </div>

          <Textarea label="Erfahrungen" value={form.custom_experience || ''} onChange={e => setForm({...form, custom_experience: e.target.value})} />

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button onClick={() => { setShowBrokerForm(false); setEditingBroker(null); }} className="px-4 py-2 border rounded-lg">Abbrechen</button>
            <button onClick={() => saveBroker(form)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"><Check className="w-5 h-5" /> Speichern</button>
          </div>
        </div>
      </Modal>
    );
  };

  // ============================================================================
  // Main Render
  // ============================================================================

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logoSvg} alt="QUND" className="h-8 w-auto" />
              <span className="text-sm font-medium text-gray-600">Broker Database</span>
            </div>
            
            <nav className="flex items-center gap-1">
              <button onClick={() => setView('dashboard')} className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${view === 'dashboard' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}>
                <BarChart3 className="w-4 h-4" /> Dashboard
              </button>
              <button onClick={() => setView('brokers')} className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${view === 'brokers' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}>
                <Building2 className="w-4 h-4" /> Broker
              </button>
              <button onClick={() => setView('lps')} className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${view === 'lps' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}>
                <Briefcase className="w-4 h-4" /> LPs
              </button>
              <button onClick={() => setView('regulators')} className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${view === 'regulators' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}>
                <Shield className="w-4 h-4" /> Regulatoren
              </button>
              <div className="h-6 w-px bg-gray-300 mx-1" />

              {/* Settings Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  onBlur={() => setTimeout(() => setSettingsOpen(false), 150)}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm ${['statuses', 'strategies', 'platforms', 'accountTypes', 'activity'].includes(view) ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'}`}
                >
                  <Settings className="w-4 h-4" /> Settings <ChevronDown className={`w-3 h-3 transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
                </button>
                {settingsOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border py-1 min-w-[160px] z-50">
                    <button onClick={() => { setView('statuses'); setSettingsOpen(false); }} className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${view === 'statuses' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}>
                      <Layers className="w-4 h-4" /> Status
                    </button>
                    <button onClick={() => { setView('strategies'); setSettingsOpen(false); }} className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${view === 'strategies' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}>
                      <Target className="w-4 h-4" /> Strategien
                    </button>
                    <button onClick={() => { setView('platforms'); setSettingsOpen(false); }} className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${view === 'platforms' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}>
                      <Monitor className="w-4 h-4" /> Plattformen
                    </button>
                    <button onClick={() => { setView('accountTypes'); setSettingsOpen(false); }} className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${view === 'accountTypes' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}>
                      <CreditCard className="w-4 h-4" /> Konten
                    </button>
                    <div className="border-t my-1" />
                    <button onClick={() => { setView('activity'); setSettingsOpen(false); }} className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${view === 'activity' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}>
                      <History className="w-4 h-4" /> Log
                    </button>
                    {isAdmin && (
                      <>
                        <div className="border-t my-1" />
                        <button onClick={() => { setView('users'); setSettingsOpen(false); }} className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${view === 'users' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}>
                          <Users className="w-4 h-4" /> Benutzer
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="h-6 w-px bg-gray-300 mx-1" />
              <button
                onClick={openSyncOptions}
                disabled={syncStatus?.status === 'running' || brokersomeSyncStatus?.status === 'running' || regulationSyncStatus?.status === 'running'}
                className="px-3 py-2 hover:bg-gray-100 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
                title="Broker Sync"
              >
                <Cloud className={`w-4 h-4 ${(syncStatus?.status === 'running' || brokersomeSyncStatus?.status === 'running' || regulationSyncStatus?.status === 'running') ? 'animate-pulse text-blue-600' : ''}`} /> Sync
              </button>
              <a href={`${API_BASE}/export/json`} className="p-2 hover:bg-gray-100 rounded-lg" title="Export JSON">
                <Download className="w-4 h-4" />
              </a>
              <button onClick={() => { loadBrokers(); loadStats(); }} className="p-2 hover:bg-gray-100 rounded-lg" title="Aktualisieren">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>

              <div className="h-6 w-px bg-gray-300 mx-1" />

              {/* User Info & Logout */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {user.username}
                  <span className={`px-1.5 py-0.5 rounded text-xs ${user.role === 1 ? 'bg-red-100 text-red-700' : user.role === 2 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                    {user.role_name}
                  </span>
                </span>
                <button onClick={handleLogout} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600" title="Abmelden">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {isOffline && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-center text-yellow-800 text-sm">
          ⚠️ Offline-Modus: Demo-Daten werden angezeigt.
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 py-6">
        {view === 'dashboard' && <DashboardView />}
        {view === 'brokers' && BrokerListView()}
        {view === 'statuses' && <SettingsView type="statuses" />}
        {view === 'regulators' && <RegulatorsView />}
        {view === 'strategies' && <SettingsView type="strategies" />}
        {view === 'platforms' && <SettingsView type="platforms" />}
        {view === 'lps' && <LPsView />}
        {view === 'accountTypes' && <AccountTypesView />}
        {view === 'activity' && <ActivityLogView key="activity" />}
        {view === 'users' && isAdmin && <UsersView />}
      </main>

      <BrokerDetailModal />
      <BrokerFormModal />

      {/* Sync Options Modal - inlined to prevent re-render focus loss */}
      {showSyncOptions && (
        <Modal isOpen={showSyncOptions} onClose={() => setShowSyncOptions(false)} title="Broker Daten synchronisieren" size="md">
          <div className="space-y-4">
            {/* Source Selection Tabs */}
            <div className="flex border-b">
              <button onClick={() => setActiveSyncSource('myfxbook')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeSyncSource === 'myfxbook' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>MyFXBook</button>
              <button onClick={() => setActiveSyncSource('brokersome')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeSyncSource === 'brokersome' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Brokersome</button>
              <button onClick={() => setActiveSyncSource('regulations')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeSyncSource === 'regulations' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Regulatoren</button>
              <button onClick={() => setActiveSyncSource('cleanup')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeSyncSource === 'cleanup' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Cleanup</button>
              <button onClick={() => setActiveSyncSource('database')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeSyncSource === 'database' ? 'border-cyan-500 text-cyan-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Datenbank</button>
            </div>

            {/* MyFXBook Options */}
            {activeSyncSource === 'myfxbook' && (
              <div className="space-y-3">
                <p className="text-gray-600 text-sm">Synchronisiert Broker-Daten von myfxbook.com</p>
                <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${selectedSyncType === 'profiles' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="syncType" value="profiles" checked={selectedSyncType === 'profiles'} onChange={() => setSelectedSyncType('profiles')} className="mt-1" />
                  <div><div className="font-medium text-gray-800">Profile aktualisieren</div><div className="text-sm text-gray-500">Aktualisiert die {totalBrokers} bestehenden Broker</div></div>
                </label>
                <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${selectedSyncType === 'full' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="syncType" value="full" checked={selectedSyncType === 'full'} onChange={() => setSelectedSyncType('full')} className="mt-1" />
                  <div><div className="font-medium text-gray-800">Vollständiger Sync</div><div className="text-sm text-gray-500">Dauert 1-2 Stunden</div></div>
                </label>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowSyncOptions(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Abbrechen</button>
                  <button onClick={() => startSync(selectedSyncType)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"><RefreshCw className="w-4 h-4" />MyFXBook Sync</button>
                </div>
              </div>
            )}

            {/* Brokersome Options */}
            {activeSyncSource === 'brokersome' && (
              <div className="space-y-3">
                <p className="text-gray-600 text-sm">Synchronisiert Daten von brokersome.com</p>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowSyncOptions(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Abbrechen</button>
                  <button onClick={startBrokersomeSync} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"><RefreshCw className="w-4 h-4" />Brokersome Sync</button>
                </div>
              </div>
            )}

            {/* Regulations Options */}
            {activeSyncSource === 'regulations' && (
              <div className="space-y-3">
                <p className="text-gray-600 text-sm">Synchronisiert Regulatoren-Daten</p>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowSyncOptions(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Abbrechen</button>
                  <button onClick={startRegulationSync} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"><RefreshCw className="w-4 h-4" />Regulatoren Sync</button>
                </div>
              </div>
            )}

            {/* Cleanup Options */}
            {activeSyncSource === 'cleanup' && (
              <div className="space-y-3">
                <p className="text-gray-600 text-sm">Prüft alle Broker-Websites auf Erreichbarkeit</p>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowSyncOptions(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Abbrechen</button>
                  <button onClick={startCleanup} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"><RefreshCw className="w-4 h-4" />Cleanup starten</button>
                </div>
              </div>
            )}

            {/* Database Sync Options */}
            {activeSyncSource === 'database' && (
              <div className="space-y-4">
                <p className="text-gray-600 text-sm">Synchronisiert die Datenbank zwischen lokaler Instanz und Remote-Server.</p>
                <div className="space-y-3 border-t pt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Remote Server URL</label>
                    <input type="text" value={dbSyncConfig.remoteUrl} onChange={e => setDbSyncConfig(c => ({ ...c, remoteUrl: e.target.value }))} placeholder="https://domain.com" className="w-full px-3 py-2 border rounded-lg text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Benutzername</label>
                      <input type="text" value={dbSyncConfig.username} onChange={e => setDbSyncConfig(c => ({ ...c, username: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Passwort</label>
                      <input type="password" value={dbSyncConfig.password} onChange={e => setDbSyncConfig(c => ({ ...c, password: e.target.value }))} placeholder="Wird nicht gespeichert" className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                  </div>
                </div>
                {dbSyncStatus.error && <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-red-700 text-sm"><strong>Fehler:</strong> {dbSyncStatus.error}</div>}
                {dbSyncStatus.status === 'loading' && <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-blue-700 text-sm flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" />Verbinde...</div>}
                {dbSyncStatus.status === 'syncing' && <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-blue-700 text-sm flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" />Synchronisiere...</div>}
                {dbSyncStatus.status === 'preview' && dbSyncStatus.preview && (
                  <div className="space-y-3 border-t pt-4">
                    <div className="font-medium text-gray-800">Vorschau:</div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-3 rounded-lg text-xs">
                        <div className="font-medium mb-1">→ Remote</div>
                        <div className="text-green-600">+ {dbSyncStatus.preview.remote?.summary?.will_add || 0} neu</div>
                        <div className="text-blue-600">↻ {dbSyncStatus.preview.remote?.summary?.will_update || 0} updates</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg text-xs">
                        <div className="font-medium mb-1">← Lokal</div>
                        <div className="text-green-600">+ {dbSyncStatus.preview.local?.summary?.will_add || 0} neu</div>
                        <div className="text-blue-600">↻ {dbSyncStatus.preview.local?.summary?.will_update || 0} updates</div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button onClick={() => runDbSync('push')} className="px-3 py-1.5 text-sm border border-cyan-300 text-cyan-700 rounded-lg hover:bg-cyan-50">Push →</button>
                      <button onClick={() => runDbSync('pull')} className="px-3 py-1.5 text-sm border border-cyan-300 text-cyan-700 rounded-lg hover:bg-cyan-50">← Pull</button>
                      <button onClick={() => runDbSync('both')} className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">↔ Beides</button>
                    </div>
                  </div>
                )}
                {dbSyncStatus.status === 'completed' && dbSyncStatus.result && (
                  <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                    <div className="font-medium text-green-800 mb-2">Sync abgeschlossen!</div>
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-2 border-t">
                  <button onClick={() => { setShowSyncOptions(false); setDbSyncStatus({ status: 'idle', preview: null, result: null, error: null }); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Schließen</button>
                  <button onClick={runDbSyncPreview} disabled={!dbSyncConfig.remoteUrl || !dbSyncConfig.password || dbSyncStatus.status === 'loading'} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center gap-2 disabled:opacity-50"><Eye className="w-4 h-4" />Vorschau</button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      <SyncModal />
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => { deleteBroker(deleteConfirm.id); setDeleteConfirm(null); }}
        title="Broker löschen?"
        message={`Möchten Sie "${deleteConfirm?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
      />
    </div>
  );
}
